import React, { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useApiClient } from "../services/api"
import { Calendar, Users, Trophy, Target, Settings, Award, Search, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import EditEventModal from "./EditEventModal"
import { EventBounties } from "./EventBounties"
import CreateBountyModal from "./CreateBountyModal"
import CustomLoader from "./CustomLoader"
import EventDetailsModal from "./EventDetailsModal"

interface CurrentProjectsProps {
  sponsorId?: string
  compact?: boolean
}

interface Project {
  id: string
  title: string
  description: string
  theme: string
  status: string
  statusColor: string
  startDate: Date | string
  endDate: Date | string
  registrationDeadline: Date | string
  maxParticipants: number
  currentParticipants: number
  totalBounties: number
  activeBounties: number
  totalTeams: number
  soloParticipants: number
  teamsWithMembers: number
}

export const CurrentProjects: React.FC<CurrentProjectsProps> = ({ sponsorId = "me", compact = false }) => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [isCreateBountyModalOpen, setIsCreateBountyModalOpen] = useState(false)
  const [selectedProjectForBounty, setSelectedProjectForBounty] = useState<Project | null>(null)
  const [selectedProjectDetailsId, setSelectedProjectDetailsId] = useState<string | null>(null)

  const [searchTerm, setSearchTerm] = useState("")
  const [bountiesLastUpdated, setBountiesLastUpdated] = useState<number>(Date.now())
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())

  const toggleCardExpansion = (projectId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }
  const apiClient = useApiClient()
  const navigate = useNavigate()

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.getCurrentProjects(sponsorId)

      if (response.success && response.data) {
        setProjects(response.data.projects)
        setError(null)
      } else {
        setError(response.error || "Failed to fetch current projects")
      }
    } catch (err) {
      setError("An error occurred while fetching projects")
      console.error("Error fetching current projects:", err)
    } finally {
      setLoading(false)
    }
  }, [sponsorId, apiClient])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const formatDate = (date: Date | string) => {
    if (!date) return "N/A"
    const d = new Date(date)
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  const getStatusBadgeStyle = (status: string) => {
    const statusLower = status.toLowerCase()
    switch (statusLower) {
      case "draft":
        return "bg-gray-100 text-gray-800 border-gray-300"
      case "published":
        return "bg-green-100 text-green-800 border-green-300"
      case "ongoing":
        return "bg-blue-100 text-blue-800 border-blue-300"
      case "completed":
        return "bg-purple-100 text-purple-800 border-purple-300"
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-300"
      default:
        return "bg-gray-100 text-gray-800 border-gray-300"
    }
  }

  const handleEditClick = (project: Project) => {
    setEditingProject(project)
    setIsEditModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsEditModalOpen(false)
    setEditingProject(null)
  }

  const handleSaveEvent = async (updatedData: any) => {
    if (!editingProject) return

    try {
      const response = await apiClient.updateHackday(editingProject.id, updatedData)

      if (response.success) {
        // Invalidate cache to force refresh
        apiClient.invalidateCache('currentprojects')
        apiClient.invalidateCache('hackdays')

        // Refresh projects list
        fetchProjects()

        handleCloseModal()
      } else {
        console.error('Failed to update event:', response.error)
        alert('Failed to update event: ' + (response.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error updating event:', error)
      alert('An error occurred while updating the event')
    }
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm("Are you sure you want to delete this event? This action cannot be undone and will remove all associated data including participants and bounties.")) {
      return
    }

    try {
      const response = await apiClient.deleteHackday(eventId)
      if (response.success) {
        // Invalidate cache to force refresh
        apiClient.invalidateCache('currentprojects')
        apiClient.invalidateCache('hackdays')
        apiClient.invalidateCache('current-events-bounties')
        apiClient.invalidateCache('sponsor-next-event')

        // Update local state by removing the deleted event
        setProjects((prev) => prev.filter((p) => p.id !== eventId))

        // Close details modal if open
        if (selectedProjectDetailsId === eventId) {
          setSelectedProjectDetailsId(null)
        }
      } else {
        alert('Failed to delete event: ' + (response.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error deleting event:', error)
      alert('An error occurred while deleting the event')
    }
  }

  const handleCreateBountyClick = (project: Project) => {
    setSelectedProjectForBounty(project)
    setIsCreateBountyModalOpen(true)
  }

  const handleCreateBountySave = async (bountyData: any) => {
    try {
      const response = await apiClient.createBounty(bountyData)

      if (response.success) {
        // Invalidate cache to force refresh
        apiClient.invalidateCache("current-events-bounties")
        apiClient.invalidateCache("bounties")
        apiClient.invalidateCache("sponsor-next-event")

        // Trigger refresh of bounties list in EventBounties
        setBountiesLastUpdated(Date.now())

        // Refresh projects list to update bounty counts
        fetchProjects()

        setIsCreateBountyModalOpen(false)
        setSelectedProjectForBounty(null)
      } else {
        console.error("Failed to create bounty:", response.error)
        alert("Failed to create bounty: " + (response.error || "Unknown error"))
      }
    } catch (error) {
      console.error('Error creating bounty:', error)
      alert('An error occurred while creating the bounty')
    }
  }

  // Filter projects based on search term
  const filteredProjects = projects.filter((project) => {
    if (searchTerm === "") return true

    const searchLower = searchTerm.toLowerCase()
    return (
      project.title.toLowerCase().includes(searchLower) ||
      project.description.toLowerCase().includes(searchLower) ||
      project.theme.toLowerCase().includes(searchLower) ||
      project.status.toLowerCase().includes(searchLower)
    )
  })

  // Sort projects to show next/upcoming events first
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const now = new Date()
    const aStart = new Date(a.startDate)
    const aEnd = new Date(a.endDate)
    const bStart = new Date(b.startDate)
    const bEnd = new Date(b.endDate)

    // Categorize events
    const getEventCategory = (start: Date, end: Date) => {
      if (start <= now && end >= now) return 1 // Active/Ongoing
      if (start > now) return 2 // Upcoming
      return 3 // Past
    }

    const aCat = getEventCategory(aStart, aEnd)
    const bCat = getEventCategory(bStart, bEnd)

    // Sort by category first (Active → Upcoming → Past)
    if (aCat !== bCat) return aCat - bCat

    // Within same category, sort by start date (earliest first for active/upcoming, latest first for past)
    if (aCat === 3) {
      // Past events: show most recent first
      return bStart.getTime() - aStart.getTime()
    } else {
      // Active/Upcoming events: show earliest first
      return aStart.getTime() - bStart.getTime()
    }
  })

  const selectedProjectForDetails = projects.find(p => p.id === selectedProjectDetailsId)

  if (loading) {
    return <CustomLoader />;
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="text-center p-8 text-red-600">{error}</div>
      </div>
    )
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="w-full">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100/50 border-2 border-dashed border-gray-300">
          <CardContent className="p-8 text-center">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Events Yet</h3>
            <p className="text-gray-600 mb-6">Create your first event to get started.</p>
            <button
              onClick={() => navigate('/sponsor/events/create')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#782c8c] text-white rounded-lg hover:opacity-90 transition-all font-medium shadow-sm"
            >
              <Plus className="h-5 w-5" />
              Create Event
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Controls - Hide in compact mode and when less than 10 events */}
      {!compact && projects.length >= 10 && (
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                style={{ colorScheme: 'light' }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">

        {/* Projects Grid */}
        <div className="grid grid-cols-1 gap-4">
          {sortedProjects.map((project) => {
            const isExpanded = expandedCards.has(project.id)
            return (
              <Card
                key={project.id}
                className={`relative overflow-hidden bg-white border-2 border-gray-200 hover:shadow-lg transition-all duration-300 ${compact ? 'border-l-4 border-l-purple-500' : ''}`}
              >
                <CardContent className={`transition-all duration-300 text-left ${isExpanded && !compact ? 'p-6' : 'p-4'}`}>
                  {!isExpanded || compact ? (
                    // COLLAPSED VIEW - Compact single row
                    <div
                      className="flex items-center justify-between gap-4 cursor-pointer"
                      onClick={() => {
                        if (compact) {
                          setSelectedProjectDetailsId(project.id)
                        } else {
                          toggleCardExpansion(project.id)
                        }
                      }}
                    >
                      {/* Left: Title + Status */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 truncate">{project.title}</h3>
                        <Badge className={`${getStatusBadgeStyle(project.status)} border font-semibold text-xs uppercase px-2 py-0.5 flex-shrink-0`}>
                          {project.status}
                        </Badge>
                      </div>

                      {/* Center: Key Metrics - Hide in compact mode */}
                      {!compact && (
                        <div className="hidden sm:flex items-center gap-6">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">{formatDate(project.startDate)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900">{project.currentParticipants}/{project.maxParticipants}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900">{project.activeBounties}</span>
                          </div>
                        </div>
                      )}

                      {/* Right: Expand button or Chevron */}
                      <button
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (compact) {
                            setSelectedProjectDetailsId(project.id)
                          } else {
                            toggleCardExpansion(project.id)
                          }
                        }}
                      >
                        {compact ? (
                          // In compact mode, show a "view details" icon or just chevron right?
                          // ChevronDown is fine, implies "open".
                          <ChevronDown className="h-5 w-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-600" />
                        )}
                      </button>
                    </div>
                  ) : (
                    // EXPANDED VIEW - Full details (Only shown if NOT compact)
                    <>
                      {/* Project Header */}
                      <div className="mb-6">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{project.title}</h3>
                              <Badge className={`${getStatusBadgeStyle(project.status)} border font-semibold text-xs uppercase px-2.5 py-0.5`}>
                                {project.status}
                              </Badge>
                            </div>
                            {project.theme && (
                              <div className="flex flex-wrap gap-1.5">
                                {project.theme.split(/[&,]+/).map((tag, i) => (
                                  <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}>
                                    {tag.trim()}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleCreateBountyClick(project)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[#782c8c] text-white rounded-lg hover:opacity-90 transition-all text-sm font-medium mr-2 shadow-sm"
                            >
                              <Plus className="h-4 w-4" />
                              Create Bounty
                            </button>
                            <button
                              onClick={() => handleEditClick(project)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit event"
                            >
                              <Settings className="h-5 w-5 text-gray-600 hover:text-gray-900" />
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(project.id)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete event"
                            >
                              <Trash2 className="h-5 w-5 text-gray-600 hover:text-red-600" />
                            </button>
                            <button
                              onClick={() => toggleCardExpansion(project.id)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Collapse"
                            >
                              <ChevronUp className="h-5 w-5 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      {project.description && (
                        <p className="text-base text-gray-600 mb-8 leading-relaxed max-w-3xl text-left">{project.description}</p>
                      )}

                      {/* Dates & Stats Row */}
                      <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-8 py-6">
                        {/* Dates */}
                        <div className="flex flex-col gap-3 min-w-[240px]">
                          <div className="flex items-center gap-3 text-sm">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-500 w-20">Dates</span>
                            <span className="text-gray-900 font-medium">
                              {formatDate(project.startDate)} - {formatDate(project.endDate)}
                            </span>
                          </div>
                          {project.registrationDeadline && (
                            <div className="flex items-center gap-3 text-sm">
                              <Target className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-500 w-20">Deadline</span>
                              <span className="text-gray-900 font-medium">
                                {formatDate(project.registrationDeadline)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-x-12 gap-y-6">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Participants</span>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-gray-900">{project.currentParticipants}</span>
                              <span className="text-sm text-gray-400">/{project.maxParticipants}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Bounties</span>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-gray-900">{project.activeBounties}</span>
                              <span className="text-sm text-gray-400">/{project.totalBounties}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Teams</span>
                            <span className="text-2xl font-bold text-gray-900">{project.totalTeams}</span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Solo</span>
                            <span className="text-2xl font-bold text-gray-900">{project.soloParticipants}</span>
                          </div>
                        </div>
                      </div>

                      {/* Event Bounties Section */}
                      <div className="mt-8 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Award className="h-5 w-5 text-amber-600" />
                            Event Bounties
                          </h4>
                        </div>
                        <EventBounties eventId={project.id} lastUpdated={bountiesLastUpdated} />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )
          })}


        </div>
      </div>




      {/* Edit Event Modal */}
      {
        isEditModalOpen && editingProject && (
          <EditEventModal
            event={editingProject}
            onClose={handleCloseModal}
            onSave={handleSaveEvent}
          />
        )
      }

      {/* Create Bounty Modal */}
      {
        isCreateBountyModalOpen && selectedProjectForBounty && (
          <CreateBountyModal
            eventId={selectedProjectForBounty.id}
            eventTitle={selectedProjectForBounty.title}
            onClose={() => {
              setIsCreateBountyModalOpen(false)
              setSelectedProjectForBounty(null)
            }}
            onSave={handleCreateBountySave}
          />
        )
      }

      {/* Event Details Modal */}
      {
        selectedProjectForDetails && (
          <EventDetailsModal
            project={selectedProjectForDetails}
            onClose={() => setSelectedProjectDetailsId(null)}
            onEdit={(p) => handleEditClick(p)}
            onDelete={(id) => handleDeleteEvent(id)}
            onCreateBounty={(p) => handleCreateBountyClick(p)}
            bountiesLastUpdated={bountiesLastUpdated}
          />
        )
      }
    </div >
  )
}

export default CurrentProjects
